import type { MemberResponse, WorkloadResponse } from '@/api/api';

/**
 * Drops members whose role sits in the `Viewer` permission group from the
 * workload roster. Viewers are read-only observers, not delivery capacity, so
 * they never get a heatmap row — even if they happen to hold an assignment.
 * Users absent from the member list (ex-members with historical assignments)
 * are kept.
 */
export function excludeViewers(
  data: WorkloadResponse,
  members: readonly MemberResponse[],
): WorkloadResponse {
  const viewerIds = new Set(
    members.filter((m) => m.permission_group === 'Viewer').map((m) => m.user_id),
  );
  if (viewerIds.size === 0) return data;
  return { ...data, users: data.users.filter((u) => !viewerIds.has(u.user_id)) };
}

export const ROSTER_SORTS = ['name_asc', 'name_desc', 'role'] as const;
export type RosterSort = (typeof ROSTER_SORTS)[number];

export const ROSTER_SORT_LABEL: Record<RosterSort, string> = {
  name_asc: 'Name A–Z',
  name_desc: 'Name Z–A',
  role: 'Role',
};

/**
 * Keeps only the selected members. Ids that no longer resolve (a stale shared
 * URL, a member since removed) are ignored; when none resolve the whole roster
 * shows rather than an empty grid.
 */
export function filterRoster(
  data: WorkloadResponse,
  selectedIds: readonly string[],
): WorkloadResponse {
  const selected = new Set(selectedIds);
  const users = data.users.filter((u) => selected.has(u.user_id));
  return users.length === 0 ? data : { ...data, users };
}

const byName = (a: { display_name: string }, b: { display_name: string }) =>
  a.display_name.localeCompare(b.display_name, undefined, { sensitivity: 'base' });

/**
 * Orders the heatmap rows. `role` follows the project's role catalog order
 * (`roleRank`: role id → position), name A–Z within a role; members whose role
 * doesn't resolve (archived role, ex-member) sink to the bottom.
 */
export function sortRoster(
  data: WorkloadResponse,
  sort: RosterSort,
  members: readonly MemberResponse[],
  roleRank: ReadonlyMap<string, number>,
): WorkloadResponse {
  const users = [...data.users];
  if (sort === 'name_desc') {
    users.sort((a, b) => byName(b, a));
  } else if (sort === 'role') {
    const rankOf = new Map(
      members.map((m) => [m.user_id, roleRank.get(m.role_id) ?? Number.POSITIVE_INFINITY]),
    );
    const rank = (id: string) => rankOf.get(id) ?? Number.POSITIVE_INFINITY;
    users.sort((a, b) => rank(a.user_id) - rank(b.user_id) || byName(a, b));
  } else {
    users.sort(byName);
  }
  return { ...data, users };
}
